import { api } from './client';

export interface ReturnSerialLookup {
  serialNo: string;
  itemId: number;
  itemName: string;
  imageUrl?: string | null;
  barcode?: string | null;
  qty: number;
  status: string;
  returnable: boolean;
  reason: string | null;
}

export interface SalesReturnLine {
  /** Scanned-serial line. */
  serialNo?: string;
  /** Quantity-based line (no scan) — resolve the article by code/id + qty. */
  itemId?: number;
  itemCode?: string;
  qty?: number;
  condition?: 'Good' | 'Damaged';
}

export interface SalesReturnItemRow {
  serialNo: string;
  itemName: string;
  qty: number;
  condition: string; // 'Good' | 'Damaged'
}
export interface SalesReturnSummary {
  id: number;
  returnNo: string;
  returnDate: string;
  invoiceId: number | null;
  invoiceNo: string | null;
  /** Online RTO receipt: Shopify order no and AWB. */
  orderRef?: string | null;
  awbNo?: string | null;
  partyName: string | null;
  remarks: string | null;
  totalQty: number;
  itemCount: number;
  goodQty: number;
  damagedQty: number;
  createdBy: string | null;
  createdAt: string;
  items: SalesReturnItemRow[];
}

export async function lookupReturnSerial(serialNo: string): Promise<ReturnSerialLookup> {
  const r = await api.get<ReturnSerialLookup>(`/sales-return/lookup/${encodeURIComponent(serialNo)}`);
  return r.data;
}

export async function listSalesReturns(): Promise<SalesReturnSummary[]> {
  const r = await api.get<SalesReturnSummary[]>('/sales-return');
  return r.data;
}

/** Path for the full return-history Excel export (no cap), in the given sort order. */
export function salesReturnsExportPath(sort?: { sortBy?: string; dir?: string }): string {
  const p = new URLSearchParams();
  if (sort?.sortBy) p.set('sortBy', sort.sortBy);
  if (sort?.dir) p.set('dir', sort.dir);
  const qs = p.toString();
  return `/sales-return/export.xlsx${qs ? `?${qs}` : ''}`;
}

export interface ReturnLocation {
  id: number;
  code: string;
  name: string;
  level: string;
  capacity: number;
  used: number;
  free: number | null;
}

export async function listReturnLocations(): Promise<ReturnLocation[]> {
  const r = await api.get<ReturnLocation[]>('/sales-return/locations');
  return r.data;
}

export interface RecentInvoice {
  invoiceNo: string;
  invoiceDate: string;
  grandTotal: number;
}

export async function listCustomerRecentInvoices(partyId: number): Promise<RecentInvoice[]> {
  const r = await api.get<RecentInvoice[]>('/sales-return/invoices', { params: { partyId } });
  return r.data;
}

export interface InvoiceExpectedItem {
  itemCode: string;
  itemName: string;
  qty: number;
  imageUrl?: string | null;
  barcode?: string | null;
}
export interface ReturnInvoiceDetail {
  invoiceNo: string;
  invoiceDate: string;
  partyId: number;
  partyName: string | null;
  grandTotal: number;
  items: InvoiceExpectedItem[];
}

/** Resolve an invoice number → its customer + expected-return articles (null if unknown). */
export async function getReturnInvoiceDetail(invoiceNo: string): Promise<ReturnInvoiceDetail | null> {
  const r = await api.get<ReturnInvoiceDetail | null>('/sales-return/invoice-detail', { params: { invoiceNo } });
  return r.data ?? null;
}

/** An online (Shopify) dispatch to receive back as RTO — found by order no or AWB. */
export interface ReturnOnlineOrder {
  outwardGrnId: number;
  grnNo: string;
  orderName: string | null;
  awbNo: string | null;
  shipmentStatus: string | null;
  partyId: number;
  customerName: string;
  /** Set when this parcel was already received back. */
  alreadyReturnedNo: string | null;
  items: InvoiceExpectedItem[];
}

export async function getReturnOnlineOrder(q: string): Promise<ReturnOnlineOrder> {
  const r = await api.get<ReturnOnlineOrder>('/sales-return/online-order', { params: { q } });
  return r.data;
}

export async function createSalesReturn(payload: {
  invoiceId?: number;
  outwardGrnId?: number;
  invoiceNo?: string;
  partyId?: number;
  remarks?: string;
  locationCode?: string;
  items: SalesReturnLine[];
}): Promise<{ id: number; returnNo: string; totalQty: number; items: number; keptAt?: string[]; sisReturnsClosed?: number }> {
  const r = await api.post('/sales-return', payload);
  return r.data;
}

/** A packed serial of an online parcel that a scanned label / barcode matched. */
export interface OnlineReturnUnit { serialNo: string; itemId: number; itemCode: string; itemName: string; maxQty: number; imageUrl: string | null; barcode: string | null }

/** Online return scan: which of this parcel's packed serials the scanned label (or item barcode) is. */
export async function lookupOnlineReturnUnit(outwardGrnId: number, code: string): Promise<{ returnable: boolean; reason: string | null; units: OnlineReturnUnit[] }> {
  const r = await api.get('/sales-return/online-unit', { params: { outwardGrnId, code } });
  return r.data;
}
